import React, { useEffect, useMemo, useRef, useState } from "react";
import { useOutletContext, useNavigate, useSearchParams } from "react-router-dom";

import { DataTableNew } from "@/components/shared/DataTable";
import useLiveResource from "@/hooks/useLiveResource";
import { Button } from "@/components/ui/Button";
import SearchInput from "@/components/shared/SearchInput";
import { Card } from "@/components/ui/Card";
import { TenantService } from "@/services/tenantService";
import { buildTenantPayload } from "./payload";
import { Eye, Pen, Trash2, Plus } from "lucide-react";
import { FilterPill, FilterSelect, Filter } from "@/components/shared/Filter";
import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";
import Skeleton from "@/components/shared/Skeleton";
import toast from "react-hot-toast";
import AddEditModel from "./components/AddEditModel";
import { ModuleService } from "@/services/moduleService";
import { GuardianService } from "@/services/guardianService";
// CV Formatting is disabled — commented out, not deleted. See PLAN doc
// "resilient-stirring-tulip".
// import { ResumeTemplateAdminService } from "@/services/resumeTemplateAdminService";
import { GUARDIAN_SWITCHES } from "@/permissions/modules";
import DeleteTenantModal from "./components/DeleteTenantModal";
import { tenantFormDefaultValues } from "./validation.schema";
import { formatDate } from "@/utils/dateUtils";
import ToggleButton from "@/components/shared/ToggleButton";
import { apiErrorMessageFormat } from "@/utils/formErrorUtils";
import CustomTooltip from "@/components/shared/CustomTooltip";

const STATUS = [
  { label: "Active", value: "true" },
  { label: "Inactive", value: "false" },
];

const IMS_SLUG_NAME = import.meta.env.VITE_IMS_SLUG_NAME ?? "";

const Tenant = () => {
  // layout header setter from outlet
  const setHeader = useOutletContext();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // search state
  const [search, setSearch] = useState("");

  // Data table silent fetch
  const [silentFetch, setSilentFetch] = useState(false);

  // filter UI state
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState(() => {
    const isActive = searchParams.get("isActive");
    return isActive === "true" || isActive === "false" ? isActive : "";
  });

  // delete modal state
  const [deletingTenantId, setDeletingTenantId] = useState(null);

  // status change tenant
  const [statusChangeTenantId, setStatusChangeTenantId] = useState(null);

  // table metadata
  const [tableData, setTableData] = useState({
    total: 0,
    isLoading: false,
  });

  // modal state (add/edit)
  const [activeModal, setActiveModal] = useState(null);
  const [editingTenant, setEditingTenant] = useState(null);

  // imperative handle onto DataTableNew, used to flip a row's toggle
  // immediately (optimistic update) independent of refetch/websocket timing
  const tableRef = useRef(null);

  /**
   * Update page header based on table data
   */
  useEffect(() => {
    setHeader({
      title: "Tenant List",
      subTitle: tableData.isLoading ? <Skeleton className="h-5 w-28" /> : `Total ${tableData.total} tenant`,
    });

    return () =>
      setHeader({
        title: "",
        subTitle: "",
      });
  }, [tableData, setHeader]);

  useEffect(() => {
    if (silentFetch) {
      setTimeout(() => {
        setSilentFetch(false);
      }, 200);
    }
  }, [silentFetch]);

  // A status toggle (or create/edit) from another SuperAdmin session
  // otherwise only shows up here on the next manual refresh.
  useLiveResource({
    keys: ["tenant"],
    onChange: () => setSilentFetch(true),
  });

  /**
   * Navigate to tenant detail view
   */
  const onHandleView = (id) => {
    navigate(`/super-admin/tenant-details/${id}`);
  };

  /**
   * Open edit modal
   */
  const onHandleEdit = (tenantRow) => {
    setEditingTenant(tenantRow);
    setActiveModal("edit");
  };

  /**
   * Open delete modal
   */
  const onHandleDelete = (id) => {
    setDeletingTenantId(id);
    setActiveModal("delete");
  };

  /**
   * Receive table data from DataTable
   */
  const onDataFetch = (data) => {
    setTableData(data);
  };

  /**
   * Toggle the status
   */
  const onHandleToggleStatus = async (id, status) => {
    // Optimistic flip: the toast and the switch agree immediately, instead
    // of waiting on a refetch that can be delayed or superseded by another
    // tab's concurrent request.
    tableRef.current?.patchRow(id, { isActive: status });
    try {
      setStatusChangeTenantId(id);
      await TenantService.statusChange(id, status);
      toast.success(status ? "Tenant activated successfully." : "Tenant deactivated successfully.");
    } catch (e) {
      console.error(e);
      tableRef.current?.patchRow(id, { isActive: !status });
      toast.error("Failed to change status.");
    } finally {
      setStatusChangeTenantId(null);
      setSilentFetch(true);
    }
  };

  /**
   * Table column configuration
   */
  const COLUMNS = [
    {
      key: "name",
      label: "Tenant Name",
      sortable: true,
      align: "left",
      render: (val) => (
        <div className="min-w-0 max-w-[220px]">
          <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-left font-medium text-[13px]" title={val}>
            {val || "—"}
          </span>
        </div>
      ),
    },
    {
      key: "slug",
      label: "Slug",
      sortable: true,
      align: "left",
      render: (val) => (
        <div className="min-w-0 max-w-[220px]">
          <span className="block overflow-hidden text-ellipsis whitespace-nowrap text-left font-medium text-[13px]" title={val}>
            {val || "—"}
          </span>
        </div>
      ),
    },
    {
      key: "region",
      label: "Country",
      sortable: true,
      align: "left",
      render: (val) => <span className="text-text-primary text-[12px]">{val ?? "—"}</span>,
    },
    {
      key: "maxUsers",
      label: "Max Users",
      sortable: true,
      align: "center",
      // A licence on invited accounts, and an AD tenant issues none — its
      // invite routes are closed and its sign-in method cannot change, so the
      // stored number caps nothing. Printing it read as a limit somebody would
      // hit; the create form does not ask for one either.
      render: (val, row) => (
        <span className="text-text-primary text-[12px]" title={row?.adLoginEnabled ? "Accounts come from Active Directory — no seat licence applies" : undefined}>
          {row?.adLoginEnabled ? "—" : (val ?? "—")}
        </span>
      ),
    },
    {
      // Status flows, not raw status count: maxCandidateStatuses caps how
      // many steps go in ONE flow, so a total-statuses-vs-that-cap ratio
      // reads as "almost maxed out" for a tenant that simply has several
      // small flows. The cap this ratio actually measures against is
      // maxCandidateStatusFlows.
      key: "statusFlows",
      label: "Status Flows",
      sortable: false,
      align: "center",
      render: (_val, row) => {
        const used = row?.totalCandidateStatusFlows ?? 0;
        const limit = row?.maxCandidateStatusFlows ?? 10;
        return (
          <span className="text-text-primary text-[12px]">
            {used} / {limit}
          </span>
        );
      },
    },
    // Currently active users in tenant.
    //
    // Not sortable on purpose. The count is not a column on `tenants` —
    // it is a count of the tenant's users, and for a tenant on its own
    // database those users are not in the platform database at all. A
    // server-side sort would read every dedicated tenant as zero and
    // silently rank them last. The Statuses column is left out for the
    // same reason.
    {
      key: "activeUsers",
      label: "Total Active Users",
      align: "center",
      sortable: false,
      render: (val) => <span className="text-left font-medium text-[13px]">{val ?? "—"}</span>,
    },
    {
      key: "isActive",
      label: "Status",
      sortable: true,
      align: "center",
      render: (val, row) => {
        const isActive = Boolean(val);
        const tenantId = row?.id;
        return (
          <div className="flex justify-center">
            <ToggleButton
              isActive={isActive}
              loading={tenantId === statusChangeTenantId}
              disabled={row?.slug === IMS_SLUG_NAME}
              onToggle={() => onHandleToggleStatus(tenantId, !isActive)}
            />
          </div>
        );
      },
    },
    {
      key: "createdAt",
      label: "Created",
      sortable: true,
      align: "center",
      render: (val) => <span className="text-text-primary text-[12px]">{val ? formatDate(val) : "—"}</span>,
    },
    {
      key: "id",
      label: "Action",
      sortable: false,
      align: "center",
      render: (val, row) => (
        <div className="flex gap-2 justify-center">
          {/* View */}
          <PermissionGuard permissions={[PERMISSIONS.SUPER_ADMIN_MANAGE, PERMISSIONS.SUPER_ADMIN_VIEW]} isComponent>
            <CustomTooltip text="View Tenant">
              <Button onClick={() => onHandleView(val)} className="px-4 py-3 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60 ">
                <Eye size={16} />
              </Button>
            </CustomTooltip>
          </PermissionGuard>

          {/* Edit — blocked for the IMS tenant and for any deactivated tenant.
                        Deactivated tenants must be reactivated (via the status toggle) before edits are allowed. */}
          <PermissionGuard permissions={[PERMISSIONS.SUPER_ADMIN_MANAGE, PERMISSIONS.SUPER_ADMIN_UPDATE]} isComponent>
            <CustomTooltip text="Edit Tenant">
              <Button
                onClick={() => onHandleEdit(row)}
                disabled={row?.slug === IMS_SLUG_NAME || !row?.isActive}
                title={!row?.isActive ? "Reactivate the tenant before editing" : undefined}
                className="px-4 py-3 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background-icon"
              >
                <Pen size={16} />
              </Button>
            </CustomTooltip>
          </PermissionGuard>

          {/* Delete */}
          <PermissionGuard permissions={[PERMISSIONS.SUPER_ADMIN_MANAGE, PERMISSIONS.SUPER_ADMIN_DELETE]} isComponent>
            <CustomTooltip text="Delete Tenant">
              <Button
                onClick={() => onHandleDelete(val)}
                disabled={row?.slug === IMS_SLUG_NAME}
                className="px-4 py-3 rounded-xl bg-background-icon text-text-primary text-sm hover:bg-background-icon/60 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-background-icon"
              >
                <Trash2 size={16} />
              </Button>
            </CustomTooltip>
          </PermissionGuard>
        </div>
      ),
    },
  ];

  /**
   * Filter configuration
   */
  const filters = useMemo(
    () => [
      {
        key: "is_active",
        label: "All Statuses",
        value: statusFilter,
        onChange: setStatusFilter,
        options: STATUS,
      },
    ],
    [statusFilter],
  );

  /**
   * Active filter pills
   */
  const activeFilters = useMemo(() => {
    const selectedStatus = STATUS.find((s) => s.value === statusFilter);

    return [
      statusFilter && {
        key: "is_active",
        label: `Status: ${selectedStatus?.label}`,
        clear: () => setStatusFilter(""),
      },
    ].filter(Boolean);
  }, [statusFilter]);

  /**
   * Clear all filters
   */
  const handleClearAll = () => {
    setStatusFilter("");
  };

  /**
   * API params for filtering
   */
  const extraParams = useMemo(
    () => ({
      isActive: statusFilter || undefined,
    }),
    [statusFilter],
  );

  /**
   * Default form values for add/edit modal
   */
  const defaultValuesForModal = useMemo(() => {
    if (activeModal === "edit" && editingTenant) {
      return {
        name: editingTenant.name ?? "",
        slug: editingTenant.slug ?? "",
        region: editingTenant.region ?? "",
        currency: editingTenant.currency ?? "",
        maxUsers: String(editingTenant.maxUsers ?? ""),
        maxCandidateStatusFlows: String(editingTenant.maxCandidateStatusFlows ?? 10),
        // Preselect the tenant's actual db_mode so the radio in
        // AddEditModel reflects reality. Without this, dedicated
        // tenants always displayed as "Shared" in edit mode.
        // Field is disabled on edit anyway — this is display-only,
        // and switching db_mode on a live tenant is Phase 2 work.
        dbMode: editingTenant.dbMode ?? "shared",
        enabledExtensionSites: editingTenant.enabledExtensionSites ?? [],
        complianceCountryMasters: editingTenant.complianceCountryMasters ?? [],
        sourcingUsesOwnApiKeys: editingTenant.sourcingUsesOwnApiKeys ?? false,
        adLoginEnabled: editingTenant.adLoginEnabled ?? false,
      };
    }

    return tenantFormDefaultValues;
  }, [activeModal, editingTenant]);

  /**
   * Submit handler for add/edit tenant
   */
  const handleSubmitForm = async (formData) => {
    const payload = buildTenantPayload(formData, {
      mode: activeModal,
      editingTenant,
    });

    try {
      if (activeModal === "edit" && editingTenant?.id) {
        await TenantService.updateTenant(editingTenant.id, payload);
        // Entitlements are a separate endpoint on purpose — the two
        // axes are never written together. Saved after the tenant so
        // a rejected tenant update cannot leave the plan changed.
        //
        // Caught here rather than by the outer handler: by this point
        // the tenant details are already written, so "Failed to save
        // tenant." was a lie. The SuperAdmin re-opened the modal, saw
        // their name change applied, and could not tell what had
        // failed. Two writes, so two outcomes to report.
        let moduleError = null;
        if (formData.modules && typeof formData.modules === "object") {
          try {
            await ModuleService.updateTenantModules(editingTenant.id, formData.modules);
          } catch (e) {
            moduleError = e;
          }
        }

        if (moduleError) {
          console.error(moduleError);
          toast.error(`Tenant details saved, but the module plan did not: ${apiErrorMessageFormat(moduleError, "the server rejected the change")}`);
        } else {
          toast.success("Tenant updated");
        }
      } else {
        // Create-only: forward dbMode ('shared' | 'dedicated').
        // Edit intentionally omits it — switching an existing
        // tenant's mode requires a data migration (Phase 2).
        payload.dbMode = formData.dbMode || "shared";
        // Module entitlements, chosen and confirmed in the modal's
        // review step. Omitted entirely when the modal could not load
        // the registry, so the server falls back to its own defaults
        // rather than creating a tenant with nothing enabled. An
        // explicit empty array IS honoured — `[]` and omission mean
        // different things.
        if (Array.isArray(formData.modules)) {
          payload.modules = formData.modules;
        }
        const res = await TenantService.createTenant(payload);

        // Prompt-safety override, chosen in the modal. It has its own
        // endpoint and needs a tenant id, so it can only be written
        // once the tenant exists. Reported separately for the same
        // reason the module plan is: the tenant is already created, so
        // "Failed to save tenant." would be a lie.
        if (formData.guardianPolicy && res?.tenant?.id) {
          try {
            await GuardianService.updateTenantPolicy(res.tenant.id, {
              [GUARDIAN_SWITCHES.MASTER]: formData.guardianPolicy,
            });
          } catch (e) {
            console.error(e);
            toast.error(
              `Tenant created, but the prompt-safety policy did not save: ${apiErrorMessageFormat(e, "the server rejected the change")}. Set it from Edit tenant.`,
            );
          }
        }

        // CV Formatting is disabled — commented out, not deleted.
        //
        // // CV template grants, chosen in the modal. Same deferral as the
        // // guardian policy above and for the same reason: the endpoint
        // // needs a tenant id that does not exist until now, so a failure
        // // here is reported on its own rather than as "Failed to add
        // // tenant" — the tenant itself is already created by this point.
        // if (Array.isArray(formData.resumeTemplateKeys) && formData.resumeTemplateKeys.length && res?.tenant?.id) {
        //   try {
        //     await ResumeTemplateAdminService.setTenantTemplates(res.tenant.id, formData.resumeTemplateKeys);
        //   } catch (e) {
        //     console.error(e);
        //     toast.error(`Tenant created, but the CV templates did not save: ${apiErrorMessageFormat(e, "the server rejected the change")}. Grant them from Edit tenant.`);
        //   }
        // }

        if (res?.provisionedDbName) {
          toast.success(`Tenant added — provisioned dedicated DB: ${res.provisionedDbName}`);
        } else if (res?.inviteError) {
          // The tenant exists and its first admin does not — the one state it
          // cannot get itself out of, since only an admin can assign a role.
          // The exact reason travels, rather than "invite failed": an AD
          // tenant's first admin is written straight in with no invitation, so
          // that word named the wrong step for half the tenants.
          toast.error(`Tenant created, but its first admin was not: ${res.inviteError}`);
        } else {
          toast.success("Tenant added");
        }
      }

      setActiveModal(null);
      setEditingTenant(null);
    } catch (e) {
      console.error(e);
      // The server's own message reaches the toast. The refusals this form can
      // earn are about the tenant it is creating — a taken slug, an unknown
      // module code — and each names the thing that is in the way. A
      // client-written "cannot do that" would read as a broken button.
      toast.error(apiErrorMessageFormat(e, "Failed to save tenant."));
    } finally {
      setSilentFetch(true);
    }
  };

  return (
    <PermissionGuard permission={PERMISSIONS.SUPER_ADMIN_MANAGE}>
      <div className="flex flex-col gap-2.5" style={{ zoom: 0.85 }}>
        {/* Search + Filters */}
        <div className="flex items-center gap-2.5">
          {/* Search */}
          <div className="flex-1">
            <SearchInput value={search} onChange={setSearch} placeholder="Search by name or slug" />
          </div>

          {/* Filters UI */}
          <Filter
            filters={filters}
            activeFilters={activeFilters}
            showFilters={showFilters}
            onToggle={() => setShowFilters((p) => !p)}
            onClearAll={handleClearAll}
            renderFilter={(f) => <FilterSelect key={f.key} {...f} />}
            renderActiveFilter={(f) => <FilterPill key={f.key} {...f} />}
          />

          {/* Add Tenant Button */}
          <PermissionGuard permission={PERMISSIONS.SUPER_ADMIN_MANAGE} isComponent>
            <Button
              onClick={() => {
                setEditingTenant(null);
                setActiveModal("add");
              }}
              className="hidden md:flex bg-button-secondary text-text-base rounded-full px-3 py-2 items-center gap-1 text-[11px] whitespace-nowrap shrink-0"
            >
              <Plus size={11} />
              Add Tenant
            </Button>
          </PermissionGuard>
        </div>

        {/* Mobile FAB */}
        <div className="fixed bottom-0 left-0 right-0 z-[110] md:hidden px-4 py-3">
          <PermissionGuard permission={PERMISSIONS.SUPER_ADMIN_MANAGE} isComponent>
            <Button
              onClick={() => {
                setEditingTenant(null);
                setActiveModal("add");
              }}
              className="w-full bg-button-secondary text-text-base rounded-full py-2.5 flex items-center justify-center gap-1.5 text-[13px]"
            >
              <Plus size={14} />
              Add Tenant
            </Button>
          </PermissionGuard>
        </div>

        {/* Table */}
        <Card className="p-0 bg-table-heading border-border-primary">
          <DataTableNew
            ref={tableRef}
            key="tenant-list"
            className="rounded-none"
            columns={COLUMNS}
            api={TenantService.fetchTenantList}
            search={search}
            dataPath="tenants"
            extraParams={extraParams}
            onDataFetch={onDataFetch}
            silentFetch={silentFetch}
          />
        </Card>

        {/* Add/Edit Modal */}
        <AddEditModel
          isOpen={activeModal === "add" || activeModal === "edit"}
          activeModal={activeModal}
          onModelClose={() => {
            setActiveModal(null);
            setEditingTenant(null);
          }}
          onHandleSubmit={handleSubmitForm}
          defaultValues={defaultValuesForModal}
          tenantId={editingTenant?.id}
          // Read-only on Edit, and not a form field: the update route carries
          // no admin, and this tenant's admins are managed on its Users screen.
          existingAdmin={editingTenant?.admin}
        />

        {/* Delete Modal */}
        <DeleteTenantModal
          open={activeModal === "delete"}
          onClose={() => {
            setActiveModal(null);
            setDeletingTenantId(null);
          }}
          tenantId={deletingTenantId}
          loading={false}
          setSilentFetch={setSilentFetch}
        />
      </div>
    </PermissionGuard>
  );
};

export default Tenant;
